import type { Message } from '@discordo/shared';
import { useCallback, useState, type ReactElement } from 'react';

import { AttachmentUpload, useAttachmentUpload } from './Attachments';
import { MessageComposer } from './MessageComposer';
import { MessageList } from './MessageList';

/**
 * The chat, as a panel pinned to the right edge instead of a row stacked in
 * the middle of the screen.
 *
 * It left the vertical flow so a shared screen can have the whole height of
 * the column: with the history sitting under the video the two split the
 * height and the screen — the thing everyone in the room is actually looking
 * at — was the one that lost. Nothing about the chat itself changed; this
 * module only decides where it lives and whether it is on screen.
 *
 * Closed, the panel renders nothing rather than hiding a mounted chat, so a
 * panel nobody opened costs no history, no composer and no scroll listener.
 */

// ---------------------------------------------------------------------------
// Remembering the choice
// ---------------------------------------------------------------------------

const STORAGE_PREFIX = 'discordo:chat-open:';

/**
 * Both directions go through try/catch. A private window can refuse storage
 * outright, and forgetting the preference must never be what breaks the
 * panel: without the answer the panel is simply closed, which is its default.
 */
function readOpen(channelId: string | null): boolean {
  if (channelId === null) {
    return false;
  }

  try {
    return window.localStorage.getItem(`${STORAGE_PREFIX}${channelId}`) === 'open';
  } catch {
    return false;
  }
}

function writeOpen(channelId: string | null, open: boolean): void {
  if (channelId === null) {
    return;
  }

  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${channelId}`, open ? 'open' : 'closed');
  } catch {
    // Nothing to recover: the panel still works for this session, it just will
    // not be remembered on the next one.
  }
}

export interface ChatPanelControl {
  open: boolean;
  toggle: () => void;
  close: () => void;
}

/**
 * Whether the panel is open for the channel being read, remembered per
 * channel: a channel you keep open for the conversation and one you keep shut
 * to watch a screen are different answers to the same question.
 */
export function useChatPanel(channelId: string | null): ChatPanelControl {
  const [remembered, setRemembered] = useState(() => ({ channelId, open: readOpen(channelId) }));

  // Switching channels re-reads that channel's own answer. Adjusting during
  // the render instead of in an effect is what keeps the panel from showing
  // the previous channel's state for a frame.
  if (remembered.channelId !== channelId) {
    setRemembered({ channelId, open: readOpen(channelId) });
  }

  const open = remembered.channelId === channelId ? remembered.open : readOpen(channelId);

  const setOpen = useCallback(
    (next: boolean) => {
      writeOpen(channelId, next);
      setRemembered({ channelId, open: next });
    },
    [channelId],
  );

  const toggle = useCallback(() => {
    setOpen(!open);
  }, [open, setOpen]);

  const close = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  return { open, toggle, close };
}

// ---------------------------------------------------------------------------
// Toggle
// ---------------------------------------------------------------------------

export interface ChatToggleProps {
  open: boolean;
  onToggle: () => void;
}

/**
 * The button that opens the panel, next to the channel name.
 *
 * A toggle button keeps one stable name and announces its state through
 * `aria-pressed`, which is what a screen reader reads back as "pressed" — the
 * same thing the highlighted background says to everyone else.
 */
export function ChatToggle({ open, onToggle }: ChatToggleProps): ReactElement {
  return (
    <button
      type="button"
      className={open ? 'chat-toggle is-active' : 'chat-toggle'}
      aria-label="Alternar o chat"
      aria-pressed={open}
      title={open ? 'Fechar o chat' : 'Abrir o chat'}
      onClick={onToggle}
    >
      <svg
        className="chat-toggle-icon"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinejoin="round"
      >
        <path d="M5 4.5h14a1.5 1.5 0 0 1 1.5 1.5v8a1.5 1.5 0 0 1-1.5 1.5H9.5l-5 4V6A1.5 1.5 0 0 1 5 4.5Z" />
      </svg>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export interface ChatPanelProps {
  channelName: string;
  messages: Message[];
  hasMore: boolean;
  loadingMessages: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => void;
  onSend: (content: string) => Promise<void> | void;
  onTyping: () => void;
  /** Who is typing, already worded; `null` when nobody is. */
  typingNotice: string | null;
  /**
   * Uploads one file to the open channel, reporting progress. The panel owns
   * that state rather than the composer because the two ways in — the clip and
   * dropping on the conversation — are in different children, and one upload
   * has to have one bar.
   */
  onSendAttachment: (file: File, onProgress?: (fraction: number) => void) => Promise<void>;
  /** Closes the panel from the backdrop — the way out on a narrow screen. */
  onClose: () => void;
}

export function ChatPanel({
  channelName,
  messages,
  hasMore,
  loadingMessages,
  loadingOlder,
  onLoadOlder,
  onSend,
  onTyping,
  typingNotice,
  onSendAttachment,
  onClose,
}: ChatPanelProps): ReactElement {
  const upload = useAttachmentUpload(onSendAttachment);

  return (
    <>
      {/*
        Only ever visible under 768px, where the panel overlays the video
        instead of taking a column of its own; the stylesheet hides it on any
        screen wide enough for the panel to push the video aside.
      */}
      <button type="button" className="chat-backdrop" aria-label="Fechar o chat" onClick={onClose} />

      <aside className="chat-panel" aria-label="Chat do canal">
        <header className="chat-panel-header">
          <h2 className="chat-panel-title">#{channelName}</h2>
        </header>

        {loadingMessages ? (
          <p className="conversation-empty">Carregando mensagens…</p>
        ) : (
          <MessageList
            messages={messages}
            hasMore={hasMore}
            loadingOlder={loadingOlder}
            onLoadOlder={onLoadOlder}
            onDropFile={upload.send}
          />
        )}

        <div className="chat-panel-foot">
          <AttachmentUpload state={upload} />
          <MessageComposer
            channelName={channelName}
            onSend={onSend}
            onTyping={onTyping}
            onAttach={upload.send}
          />
          <p className="typing-notice" aria-live="polite">
            {typingNotice ?? ''}
          </p>
        </div>
      </aside>
    </>
  );
}

export default ChatPanel;
